import { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { format } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import {
  pickEndHHMMFromStart,
  composeUtcIso,
  addDaysLocal,
} from "../utils/mark-time";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { DatePicker } from "../../../components/ui/date-picker";
import { TimePicker } from "./TimePicker.jsx";
import {
  createAvailabilityMark,
  deleteAvailabilityMark,
  updateAvailabilityMark,
} from "../../../lib/data/availability";
import { useUserSettings } from "../../../hooks/useUserSettings";
import { useAutoVoteSettings } from "../../../hooks/useAutoVoteSettings";
import { cn } from "../../../lib/utils";
import { inclusiveLastDayYmd } from "../../../lib/all-day";
import { RRuleForm } from "./RRuleForm.jsx";
import { AdvancedFieldsSection } from "./AdvancedFieldsSection.jsx";

const LEVELS = [
  { value: "PREFERRED", label: "Preferred" },
  { value: "FEASIBLE", label: "Feasible" },
];

const WEEKDAYS = [
  { short: "Mon", code: "MO" },
  { short: "Tue", code: "TU" },
  { short: "Wed", code: "WE" },
  { short: "Thu", code: "TH" },
  { short: "Fri", code: "FR" },
  { short: "Sat", code: "SA" },
  { short: "Sun", code: "SU" },
];

/** Pull the BYDAY codes (e.g. ["MO","WE"]) out of an RRULE string. */
function extractByDay(rruleStr) {
  const m = /BYDAY=([A-Z,]+)/i.exec(rruleStr || "");
  if (!m) return [];
  return m[1].split(",").filter(Boolean);
}

/**
 * Rewrite (or drop) the BYDAY clause of an RRULE string. Empty `codes` removes
 * the clause entirely — rrule.js requires a non-empty list when present.
 */
function rewriteByDay(rruleStr, codes) {
  const sanitized = (codes || []).filter(Boolean);
  if (sanitized.length === 0) {
    return (rruleStr || "").replace(/;?BYDAY=[A-Z,]+/i, "");
  }
  if (/BYDAY=/i.test(rruleStr || "")) {
    return rruleStr.replace(/BYDAY=[A-Z,]+/i, `BYDAY=${sanitized.join(",")}`);
  }
  const base = rruleStr && rruleStr.length > 0 ? rruleStr : "FREQ=WEEKLY";
  return `${base};BYDAY=${sanitized.join(",")}`;
}

// ---------------------------------------------------------------------------
// Module-private helpers
// ---------------------------------------------------------------------------

function toMin(s) {
  if (s === "24:00") return 24 * 60;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
}

/** Parse "YYYY-MM-DD" into a local-midnight Date (no timezone shifting). */
function parseLocalDate(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  const [y, m, d] = yyyyMmDd.split("-").map((s) => Number.parseInt(s, 10));
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

/**
 * "End-of-day sentinel" detection: returns true iff the end timestamp is
 * exactly the start's next-calendar-day at 00:00 in the given timezone.
 * Used at load-time to re-display single-day marks-with-midnight-end as the
 * synthetic 24:00 sentinel rather than as a multi-day event.
 */
function isExactlyNextDayMidnight(startUtc, endUtc, tzid) {
  try {
    const startDateStr = formatInTimeZone(startUtc, tzid, "yyyy-MM-dd");
    const endStr = formatInTimeZone(endUtc, tzid, "yyyy-MM-dd'T'HH:mm");
    // Build "expected" by adding a day in the local tz (DST-safe: walk via
    // a date built from startDateStr's components in the local tz).
    const startLocal = parseLocalDate(startDateStr);
    const expectedLocal = addDaysLocal(startLocal, 1);
    const expectedStr = `${format(expectedLocal, "yyyy-MM-dd")}T00:00`;
    return endStr === expectedStr;
  } catch {
    return false;
  }
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * MarkEditorDialog (B12 redesign) — Google-Calendar-style layout for authoring
 * an availability mark.
 *
 * Layout:
 *   [• Preferred  • Feasible]
 *   [📅 Date]  [🕐 Start ▾] – [🕑 End ▾]
 *   [□ All-day]   [□ Multi-day]   (mutually exclusive)
 *   [📅 End date]  ← only when Multi-day is on
 *   [Repeat …]    + iCal preview
 *   Footer:
 *     create: [Cancel] [Create]
 *     edit:   [Delete*]                   [Cancel] [Save]
 *
 * The "12:00 AM (end of day)" / 24:00 sentinel is supported as a single-day
 * end value; on save we emit it as next-day-00:00 UTC so RBC + downstream
 * consumers see a normal forward-running interval.
 */
export function MarkEditorDialog({
  open,
  onOpenChange,
  uid,
  mode,
  initial,
  onSaved,
}) {
  const { defaultMarkDurationHours } = useUserSettings();
  const autoVoteSettings = useAutoVoteSettings(uid);
  const advancedDefaults = useMemo(
    () => ({
      leewayFrontMinutes: autoVoteSettings.settings?.defaultLeewayFrontMinutes ?? 0,
      leewayBackMinutes: autoVoteSettings.settings?.defaultLeewayBackMinutes ?? 0,
      fitMode: autoVoteSettings.settings?.defaultFitMode ?? "full-duration",
      appliesToQuestingGroupIds: null,
    }),
    [autoVoteSettings.settings]
  );
  const tzid =
    initial?.tzid || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ---- Local state -------------------------------------------------------
  const [title, setTitle] = useState("");
  const [level, setLevel] = useState("PREFERRED");
  const [allDay, setAllDay] = useState(false);
  const [multiDay, setMultiDay] = useState(false);
  const [startDate, setStartDate] = useState(() => parseLocalDate(format(new Date(), "yyyy-MM-dd")));
  const [endDate, setEndDate] = useState(() => parseLocalDate(format(new Date(), "yyyy-MM-dd")));
  const [startHHMM, setStartHHMM] = useState("17:00");
  const [endHHMM, setEndHHMM] = useState("18:00");
  const [rrule, setRrule] = useState(null);
  const [rruleUntil, setRruleUntil] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Phase 3c-2 U3: advanced fields (leeway, fit-mode, group scoping). On
  // create, pre-fill from autoVoteSettings defaults; on edit, hydrate from
  // the mark's stored values in the resync effect below.
  const [advanced, setAdvanced] = useState(advancedDefaults);

  // ---- Resync from `initial` whenever the dialog opens -------------------
  useEffect(() => {
    if (!open || !initial) return;
    const localTzid = initial.tzid || tzid;

    setLevel(initial.level || "PREFERRED");
    setTitle(initial.title || "");

    const startUtc = new Date(initial.startUtc);
    const endUtc = new Date(initial.endUtc);
    const startStr = formatInTimeZone(startUtc, localTzid, "yyyy-MM-dd");
    const endStr = formatInTimeZone(endUtc, localTzid, "yyyy-MM-dd");

    setStartDate(parseLocalDate(startStr));

    // For all-day marks, storage uses exclusive-end (next-day-midnight). The
    // user-facing end date is the inclusive last day, so subtract 1 ms before
    // formatting. (Same trick `buildAvailabilityCalendarEvents` uses for RBC.)
    const inclusiveEndStr = initial.allDay
      ? inclusiveLastDayYmd(endUtc, localTzid)
      : endStr;
    setEndDate(parseLocalDate(inclusiveEndStr));

    setAllDay(!!initial.allDay);

    const isMidnightSentinel = isExactlyNextDayMidnight(
      startUtc,
      endUtc,
      localTzid
    );
    // multiDay is INDEPENDENT of allDay now: it just means start and end
    // calendar dates differ.
    const userEndStr = initial.allDay ? inclusiveEndStr : endStr;
    setMultiDay(startStr !== userEndStr && !isMidnightSentinel);

    if (initial.allDay) {
      setStartHHMM("00:00");
      setEndHHMM("00:00");
    } else if (isMidnightSentinel) {
      setStartHHMM(formatInTimeZone(startUtc, localTzid, "HH:mm"));
      setEndHHMM("24:00");
    } else {
      setStartHHMM(formatInTimeZone(startUtc, localTzid, "HH:mm"));
      setEndHHMM(formatInTimeZone(endUtc, localTzid, "HH:mm"));
    }

    setRrule(initial.rrule || null);
    setRruleUntil(initial.rruleUntil || null);
    setConfirmingDelete(false);
    setBusy(false);
    setError(null);
    // Hydrate advanced fields from the mark's stored values; missing fields
    // fall back to user-default settings (advancedDefaults). On create
    // (initial without an id) we keep advancedDefaults verbatim.
    setAdvanced({
      leewayFrontMinutes:
        typeof initial.leewayFrontMinutes === "number"
          ? initial.leewayFrontMinutes
          : advancedDefaults.leewayFrontMinutes,
      leewayBackMinutes:
        typeof initial.leewayBackMinutes === "number"
          ? initial.leewayBackMinutes
          : advancedDefaults.leewayBackMinutes,
      fitMode: initial.fitMode || advancedDefaults.fitMode,
      appliesToQuestingGroupIds: Array.isArray(initial.appliesToQuestingGroupIds)
        ? initial.appliesToQuestingGroupIds
        : null,
    });
    // We intentionally re-sync only on `open` transitions; `initial` is a
    // stable parent-owned prop and we don't want to clobber in-flight edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- Derived: dtstart for RRuleForm -----------------------------------
  const dtstartForRrule = useMemo(() => {
    try {
      if (!startDate) return new Date();
      const dateStr = format(startDate, "yyyy-MM-dd");
      const time = allDay ? "00:00" : startHHMM === "24:00" ? "00:00" : startHHMM;
      return fromZonedTime(`${dateStr}T${time}`, tzid);
    } catch {
      if (initial?.startUtc) return new Date(initial.startUtc);
      return new Date();
    }
  }, [allDay, startDate, startHHMM, tzid, initial?.startUtc]);

  // ---- Derived: is recurrence active? -----------------------------------
  const rruleActive = rrule != null && rrule.length > 0;
  const selectedByDay = useMemo(() => extractByDay(rrule || ""), [rrule]);

  function toggleByDay(code) {
    const next = selectedByDay.includes(code)
      ? selectedByDay.filter((c) => c !== code)
      : [...selectedByDay, code];
    setRrule(rewriteByDay(rrule || "FREQ=WEEKLY;BYDAY=MO", next));
  }

  // ---- Toggle handlers ---------------------------------------------------
  // All-day and Multi-day are INDEPENDENT toggles UNLESS RRULE is active —
  // then they become mutually exclusive (per Task D13). Valid combinations
  // without RRULE:
  //   (¬allDay, ¬multiDay): single day with start/end times.
  //   ( allDay, ¬multiDay): single all-day on one calendar date.
  //   (¬allDay,  multiDay): two dates with start/end times on each.
  //   ( allDay,  multiDay): two-date span with no times (e.g. vacation block).
  function handleAllDayChange(next) {
    if (next && rruleActive && multiDay) setMultiDay(false);
    setAllDay(next);
    if (!next && (!startHHMM || startHHMM === "00:00")) {
      // Coming out of all-day with empty times → seed sensible defaults.
      const initStart = "17:00";
      setStartHHMM(initStart);
      setEndHHMM(pickEndHHMMFromStart(initStart, defaultMarkDurationHours));
    }
  }

  function handleMultiDayChange(next) {
    if (next && rruleActive && allDay) setAllDay(false);
    if (next) {
      setMultiDay(true);
      if (endDate && startDate && endDate < startDate) {
        setEndDate(startDate);
      }
      return;
    }
    setMultiDay(false);
    setEndDate(startDate);
    if (!allDay && toMin(endHHMM) <= toMin(startHHMM)) {
      setEndHHMM(pickEndHHMMFromStart(startHHMM, defaultMarkDurationHours));
    }
  }

  function handleStartDateChange(next) {
    if (!next) return;
    setStartDate(next);
    if (!multiDay) setEndDate(next);
    else if (endDate && next > endDate) setEndDate(next);
  }

  function handleStartTimeChange(next) {
    setStartHHMM(next);
    if (!multiDay && toMin(endHHMM) <= toMin(next)) {
      setEndHHMM(pickEndHHMMFromStart(next, defaultMarkDurationHours));
    }
  }

  // ---- Save / delete -----------------------------------------------------
  function buildPayload() {
    let startUtc = null;
    let endUtc = null;
    if (allDay) {
      startUtc = composeUtcIso(startDate, "00:00", tzid);
      // Exclusive-end convention: store end as next-day-midnight of endDate
      // (which equals startDate when single-day all-day).
      endUtc = composeUtcIso(addDaysLocal(endDate, 1), "00:00", tzid);
    } else if (multiDay) {
      startUtc = composeUtcIso(startDate, startHHMM, tzid);
      endUtc = composeUtcIso(endDate, endHHMM, tzid);
    } else {
      startUtc = composeUtcIso(startDate, startHHMM, tzid);
      if (endHHMM === "24:00") {
        endUtc = composeUtcIso(addDaysLocal(startDate, 1), "00:00", tzid);
      } else {
        endUtc = composeUtcIso(startDate, endHHMM, tzid);
      }
    }
    return {
      level,
      title: title.trim() || null,
      startUtc,
      endUtc,
      tzid,
      allDay,
      rrule,
      rruleUntil,
      exceptions: initial?.exceptions || [],
      appliesToQuestingGroupIds: advanced.appliesToQuestingGroupIds,
      leewayFrontMinutes: advanced.leewayFrontMinutes,
      leewayBackMinutes: advanced.leewayBackMinutes,
      fitMode: advanced.fitMode,
    };
  }

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (mode === "edit" && initial?.id) {
        await updateAvailabilityMark(uid, initial.id, payload);
      } else {
        await createAvailabilityMark(uid, payload);
      }
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to save mark.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!initial?.id) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAvailabilityMark(uid, initial.id);
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to delete mark.");
      setConfirmingDelete(false);
    } finally {
      setBusy(false);
    }
  }

  const dialogHeading =
    mode === "edit" ? "Edit availability mark" : "New availability mark";
  const primaryLabel = mode === "edit" ? "Save" : "Create";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogHeading}</DialogTitle>
          <DialogDescription>
            Choose a level, time range, and optional recurrence. All times are
            saved in your linked calendar&rsquo;s timezone.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="rounded-xl bg-rose-100 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="space-y-4 py-2">
          {/* Optional title */}
          <div className="space-y-1.5">
            <label
              htmlFor="mark-title"
              className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Title <span className="lowercase text-slate-400">(optional)</span>
            </label>
            <input
              id="mark-title"
              type="text"
              maxLength={80}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
              placeholder="e.g. D&D window, gym, dinner break"
              className="block w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Level radios */}
          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Level
            </legend>
            <div className="mt-2 flex gap-4">
              {LEVELS.map((lvl) => (
                <label
                  key={lvl.value}
                  className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    type="radio"
                    name="level"
                    aria-label={lvl.label}
                    value={lvl.value}
                    checked={level === lvl.value}
                    onChange={() => setLevel(lvl.value)}
                    disabled={busy}
                  />
                  {lvl.label}
                </label>
              ))}
            </div>
          </fieldset>

          {/* Date + time row(s) */}
          {multiDay ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-[5.5rem] shrink-0 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Start date
                </span>
                <div className="min-w-[10rem]">
                  <DatePicker
                    date={startDate}
                    onSelect={handleStartDateChange}
                    placeholder="Pick a date"
                    aria-label="Start date"
                  />
                </div>
                {!allDay && (
                  <TimePicker
                    kind="start"
                    value={startHHMM}
                    onChange={handleStartTimeChange}
                    ariaLabel="Start time"
                    disabled={busy}
                  />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-[5.5rem] shrink-0 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  End date
                </span>
                <div className="min-w-[10rem]">
                  <DatePicker
                    date={endDate}
                    onSelect={(d) => d && setEndDate(d)}
                    placeholder="Pick a date"
                    aria-label="End date"
                  />
                </div>
                {!allDay && (
                  <TimePicker
                    kind="end"
                    value={endHHMM}
                    startTime={null}
                    onChange={(v) => setEndHHMM(v)}
                    ariaLabel="End time"
                    disabled={busy}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[10rem]">
                <DatePicker
                  date={startDate}
                  onSelect={handleStartDateChange}
                  placeholder="Pick a date"
                  aria-label="Start date"
                />
              </div>
              {!allDay && (
                <>
                  <TimePicker
                    kind="start"
                    value={startHHMM}
                    onChange={handleStartTimeChange}
                    ariaLabel="Start time"
                    disabled={busy}
                  />
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    &ndash;
                  </span>
                  <TimePicker
                    kind="end"
                    value={endHHMM}
                    startTime={startHHMM}
                    onChange={(v) => setEndHHMM(v)}
                    ariaLabel="End time"
                    disabled={busy}
                  />
                </>
              )}
            </div>
          )}

          {/* Independent toggles — combinations: none / All-day / Multi-day / both.
              Mutually exclusive when RRULE is active (Task D13). */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                aria-label="All-day"
                checked={allDay}
                onChange={(e) => handleAllDayChange(e.target.checked)}
                disabled={busy || (rruleActive && multiDay)}
                className="disabled:cursor-not-allowed"
              />
              All-day
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                aria-label="Multi-day"
                checked={multiDay}
                onChange={(e) => handleMultiDayChange(e.target.checked)}
                disabled={busy || (rruleActive && allDay)}
                className="disabled:cursor-not-allowed"
              />
              Multi-day
            </label>
          </div>

          {/* Weekday picker (All-day + RRULE only) — drives BYDAY directly. */}
          {allDay && rruleActive && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-600 dark:text-slate-300">
                Repeat on:
              </span>
              {WEEKDAYS.map(({ short, code }) => {
                const pressed = selectedByDay.includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => toggleByDay(code)}
                    aria-pressed={pressed}
                    disabled={busy}
                    className={cn(
                      "rounded-full px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                      pressed
                        ? "bg-brand-primary text-white"
                        : "border border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300"
                    )}
                  >
                    {short}
                  </button>
                );
              })}
            </div>
          )}

          <RRuleForm
            value={{ rrule, rruleUntil }}
            dtstart={dtstartForRrule}
            tzid={tzid}
            onChange={(next) => {
              setRrule(next.rrule);
              setRruleUntil(next.rruleUntil);
            }}
          />

          <AdvancedFieldsSection
            value={advanced}
            defaults={advancedDefaults}
            onChange={setAdvanced}
          />
        </div>

        <DialogFooter className="sm:justify-between">
          {mode === "edit" ? (
            !confirmingDelete ? (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                disabled={busy}
                className="flex shrink-0 items-center gap-1.5 rounded-xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/50 dark:text-rose-300 dark:hover:bg-rose-900/30"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                Delete
              </button>
            ) : (
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy}
                  className="rounded-xl bg-rose-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-rose-700 disabled:opacity-50"
                >
                  Confirm delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  aria-label="Cancel delete"
                >
                  Cancel
                </button>
              </div>
            )
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {busy ? "Saving…" : primaryLabel}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MarkEditorDialog;
